<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'

import axios from 'axios'
import { User, Image as ImageIcon, ChevronLeft, Loader2, RefreshCw, LogOut, Search, Eye } from 'lucide-vue-next'
import Login from './components/Login.vue'

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3007/api' : '/api'
const people = ref([])
const showPreviews = ref(false)
const searchQuery = ref('')
const selectedPerson = ref(null)
const personImages = ref([])
const deepSearchFolders = ref([])
const searchingImages = ref(false)
const displayedImageCount = ref(12)
const loading = ref(false)
const refreshing = ref(false)
const scrollSentinel = ref(null)
const currentUser = ref(null)
const isLoggedIn = ref(false)
let observer = null
let searchTimeout = null

const filteredPeople = computed(() => {
  if (!searchQuery.value) return people.value
  const query = searchQuery.value.toLowerCase()
  return people.value.filter(p => 
    p.name.toLowerCase().includes(query) || 
    p.bucket.toLowerCase().includes(query)
  )
})

const searchGlobalImages = async () => {
  if (!searchQuery.value) {
    deepSearchFolders.value = []
    return
  }
  
  searchingImages.value = true
  try {
    const res = await axios.get(`${API_BASE}/search-images`, {
      params: { q: searchQuery.value }
    })
    deepSearchFolders.value = res.data
  } catch (err) {
    console.error('Deep search failed', err)
  } finally {
    searchingImages.value = false
  }
}

watch(searchQuery, (newQuery) => {
  clearTimeout(searchTimeout)
  deepSearchFolders.value = [] // Reset when typing
  
  if (newQuery && filteredPeople.value.length === 0) {
    searchTimeout = setTimeout(searchGlobalImages, 600)
  }
})

const visibleImages = computed(() => {
  return personImages.value.slice(0, displayedImageCount.value)
})

const loadMore = () => {
  if (displayedImageCount.value < personImages.value.length) {
    displayedImageCount.value += 12
  }
}

watch(scrollSentinel, (newVal) => {
  if (observer) observer.disconnect()
  if (!newVal) return

  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      loadMore()
    }
  }, { threshold: 0.1, rootMargin: '200px' })
  
  observer.observe(newVal)
})




const fetchPeople = async (forceRefresh = false) => {
  if (forceRefresh) refreshing.value = true
  else loading.value = true
  
  try {
    if (forceRefresh) {
      await axios.post(`${API_BASE}/refresh`)
    }
    const res = await axios.get(`${API_BASE}/people`)
    people.value = res.data
  } catch (err) {
    console.error('Failed to fetch people', err)
  } finally {
    loading.value = false
    refreshing.value = false
  }
}


const selectPerson = async (person, pushState = true) => {
  selectedPerson.value = person
  displayedImageCount.value = 12
  loading.value = true

  if (pushState) {
    window.history.pushState(
      { bucket: person.bucket, name: person.name },
      '',
      `/${person.bucket}/${person.name}`
    )
  }

  try {
    const res = await axios.get(`${API_BASE}/people/${person.bucket}/${person.name}`)
    personImages.value = res.data
  } catch (err) {
    console.error('Failed to fetch images', err)
  } finally {
    loading.value = false
  }
}

const goBack = (pushState = true) => {
  selectedPerson.value = null
  personImages.value = []
  if (pushState) {
    window.history.pushState({}, '', '/')
  }
}

const handleLoginSuccess = (user) => {
  currentUser.value = user
  isLoggedIn.value = true
  initializeFromUrl()
}

const handleLogout = () => {
  localStorage.removeItem('user')
  currentUser.value = null
  isLoggedIn.value = false
  people.value = []
  selectedPerson.value = null
  window.history.pushState({}, '', '/')
}

const initializeFromUrl = async () => {
  await fetchPeople()
  
  const path = window.location.pathname
  const parts = path.split('/').filter(Boolean)
  
  if (parts.length >= 2) {
    const bucket = parts[0]
    const name = parts[1]
    const person = people.value.find(p => p.bucket === bucket && p.name === name)
    if (person) {
      selectPerson(person, false)
    }
  }
}

onMounted(() => {
  const savedUser = localStorage.getItem('user')
  if (savedUser) {
    currentUser.value = JSON.parse(savedUser)
    isLoggedIn.value = true
    initializeFromUrl()
  }

  window.addEventListener('popstate', (event) => {
    if (event.state && event.state.bucket && event.state.name) {
      const person = people.value.find(p => p.bucket === event.state.bucket && p.name === event.state.name)
      if (person) selectPerson(person, false)
    } else {
      goBack(false)
    }
  })
})
</script>

<template>
  <div v-if="!isLoggedIn">
    <Login @login-success="handleLoginSuccess" />
  </div>

  <div v-else class="container">
    <header v-if="!selectedPerson">
      <div class="header-main">
        <div class="header-left">
          <div class="header-titles">
            <h1>CloudFly Gallery</h1>
            <p class="subtitle">Explore image collections stored in CloudFly S3</p>
          </div>
          <div class="search-box">
            <Search :size="18" class="search-icon" />
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search folders or buckets..." 
              class="search-input"
            />
          </div>
        </div>
        <div class="header-actions">
          <button 
            v-if="!showPreviews" 
            @click="showPreviews = true" 
            class="btn-previews"
          >
            <Eye :size="18" />
            Show Previews
          </button>
          <button 
            @click="fetchPeople(true)" 
            class="btn-refresh" 
            :disabled="refreshing"
          >
            <RefreshCw :size="18" :class="{ 'animate-spin': refreshing }" />
            {{ refreshing ? 'Refreshing...' : 'Refresh' }}
          </button>
          <button @click="handleLogout" class="btn-logout" title="Sign Out">
            <LogOut :size="18" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </header>


    <header v-else class="header-back">
      <div class="header-back-left">
        <button @click="goBack" class="btn-back">
          <ChevronLeft :size="20" />
          Back
        </button>
        <h2>{{ selectedPerson.name }}</h2>
      </div>
      <button @click="handleLogout" class="btn-logout" title="Sign Out">
        <LogOut :size="18" />
      </button>
    </header>

    <!-- Loading State -->
    <div v-if="loading" class="loader-container">
      <Loader2 class="animate-spin" :size="48" />
    </div>

    <!-- People Grid -->
    <div v-if="!selectedPerson && !loading" class="people-section">
      <div v-if="filteredPeople.length > 0" class="grid-people">
        <div 
          v-for="person in filteredPeople" 
          :key="person.name" 
          class="glass-card person-card"
          :class="{ 'has-preview': showPreviews && person.previewUrl }"
          @click="selectPerson(person)"
        >
          <span class="image-badge" title="Image count">
            <ImageIcon :size="12" />
            {{ person.imageCount }}
          </span>

          <div v-if="showPreviews && person.previewUrl" class="preview-wrapper">
            <img :src="person.previewUrl" :alt="person.name" loading="lazy" />
          </div>
          <div v-else class="icon-wrapper">
            <User :size="40" color="#818cf8" />
          </div>
          <div class="person-info">
            <h3>{{ person.name }}</h3>
            <p>{{ person.bucket }}</p>
          </div>
        </div>
      </div>
      
      <div v-else class="search-fallback">
        <!-- Folders containing these images -->
        <div v-if="deepSearchFolders.length > 0" class="global-results">
          <div class="results-header">
            <ImageIcon :size="24" />
            <h2>Folders containing matching images</h2>
            <span class="count">{{ deepSearchFolders.length }} found</span>
          </div>
          <div class="grid-people">
            <div 
              v-for="person in deepSearchFolders" 
              :key="person.name" 
              class="glass-card person-card"
              @click="selectPerson(person)"
            >
              <span v-if="person.imageCount" class="image-badge">
                <ImageIcon :size="12" />
                {{ person.imageCount }}
              </span>
              <div class="icon-wrapper">
                <User :size="40" color="#818cf8" />
              </div>
              <div class="person-info">
                <h3>{{ person.name }}</h3>
                <p>{{ person.bucket }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- No Results at All -->
        <div v-else-if="!searchingImages" class="no-results">
          <Search :size="48" color="#4b5563" />
          <p>No matches found for "{{ searchQuery }}"</p>
          <button @click="searchQuery = ''" class="btn-clear" style="margin-top: 1rem;">Clear Search</button>
        </div>

        <!-- Searching Global State -->
        <div v-else class="searching-global">
          <Loader2 class="animate-spin" :size="32" />
          <p>Deep searching images for "{{ searchQuery }}"...</p>
        </div>
      </div>
    </div>

    <!-- Image Grid -->
    <div v-if="selectedPerson && !loading" class="image-gallery">
      <div class="masonry-grid">
        <div v-for="img in visibleImages" :key="img.key" class="glass-card image-card">
          <img :src="img.url" :alt="img.name" loading="lazy" />
        </div>
      </div>
      
      <!-- Sentinel for Infinite Scroll -->
      <div v-if="visibleImages.length < personImages.length" ref="scrollSentinel" class="scroll-sentinel">
        <Loader2 class="animate-spin" :size="24" />
        <span>Loading more images...</span>
      </div>

      <div v-if="personImages.length === 0" class="no-images">
        <ImageIcon :size="48" color="#4b5563" />
        <p>No images found in this folder</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
  margin-bottom: 3rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  flex: 1;
}

.search-box {
  position: relative;
  max-width: 500px;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #4b5563;
  pointer-events: none;
}

.search-input {
  width: 100%;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border-radius: 0.75rem;
  color: white;
  font-size: 1rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  background: rgba(30, 41, 59, 0.8);
  border-color: var(--primary);
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  padding-bottom: 2px;
}

@media (max-width: 850px) {
  .header-main {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }
  
  .search-box {
    max-width: 100%;
  }
}

@media (max-width: 640px) {
  .header-actions {
    flex-direction: column;
  }
  
  .btn-refresh, .btn-logout {
    justify-content: center;
    width: 100%;
  }
  
  .container {
    padding: 1rem;
  }
  
  h1 {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }
}

.btn-refresh, .btn-logout, .btn-previews {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text);
  padding: 0.75rem 1.25rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-previews {
  background: rgba(99, 102, 241, 0.1);
  border-color: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
}

.btn-previews:hover {
  background: rgba(99, 102, 241, 0.2);
  border-color: rgba(99, 102, 241, 0.4);
}

.btn-logout {
  border-color: rgba(239, 68, 68, 0.2);
}

.btn-logout:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.4);
  color: #fca5a5;
}

.btn-refresh:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.subtitle {
  color: var(--text-muted);
  margin-bottom: 0;
}

.header-back {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.header-back-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.btn-back {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.05);
}

.loader-container {
  display: flex;
  justify-content: center;
  padding: 4rem;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.grid-people {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1.5rem;
}

@media (max-width: 480px) {
  .grid-people {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

.person-card {
  position: relative;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.3s ease;
}

.person-card.has-preview {
  padding: 0;
  overflow: hidden;
  text-align: left;
}

.preview-wrapper {
  width: 100%;
  height: 140px;
  overflow: hidden;
  background: #0f172a;
}

.preview-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.person-card:hover .preview-wrapper img {
  transform: scale(1.1);
}

.person-info {
  padding: 1rem;
}

.person-card.has-preview h3 {
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.image-badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 10;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #a5b4fc;
  padding: 0.35rem 0.6rem;
  border-radius: 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.icon-wrapper {
  background: rgba(129, 140, 248, 0.1);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.masonry-grid {
  columns: 4 250px;
  column-gap: 1.5rem;
}

.image-card {
  break-inside: avoid;
  margin-bottom: 1.5rem;
  overflow: hidden;
  position: relative;
  min-height: 250px;
  background: rgba(15, 23, 42, 0.3);
}

.image-card img {
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.5s ease;
}

.image-card:hover img {
  transform: scale(1.05);
}

@media (max-width: 1024px) {
  .masonry-grid {
    columns: 3 250px;
  }
}

@media (max-width: 768px) {
  .masonry-grid {
    columns: 2 200px;
  }
}

@media (max-width: 480px) {
  .masonry-grid {
    columns: 1;
  }
}

.image-info {
  padding: 0.75rem;
  background: rgba(15, 23, 42, 0.82);
  font-size: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.img-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

.img-folder {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.global-results {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.results-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  color: var(--text-muted);
}

.results-header h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
}

.results-header .count {
  background: rgba(99, 102, 241, 0.1);
  color: var(--primary);
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.searching-global {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 6rem 0;
  color: var(--text-muted);
}

.no-results {
  text-align: center;
  padding: 6rem 2rem;
  color: var(--text-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  background: rgba(30, 41, 59, 0.2);
  border-radius: 1.5rem;
  border: 1px dashed rgba(255, 255, 255, 0.1);
}

.btn-clear {
  background: rgba(99, 102, 241, 0.1);
  color: var(--primary);
  border: 1px solid rgba(99, 102, 241, 0.2);
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-clear:hover {
  background: var(--primary);
  color: white;
}

.no-images {
  text-align: center;
  padding: 4rem;
  color: var(--text-muted);
}

.scroll-sentinel {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  color: var(--text-muted);
}
</style>

